import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  BarChart3,
  Shield,
  LogOut,
} from 'lucide-react';

const BetaDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [engineMetrics, setEngineMetrics] = useState(null);
  const [allBots, setAllBots] = useState([]);
  const [strategyConfidence, setStrategyConfidence] = useState({ overall: 87, alpha: 92, momentum: 85, defensive: 88 });
  const [alerts, setAlerts] = useState([]);
  const [betaMetrics, setBetaMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(() => loadDashboard(), 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const [userRes, botsRes, metricsRes] = await Promise.all([
        api.getMe(),
        api.getBots(),
        api.getEngineMetrics().catch(() => ({ data: null })),
      ]);
      
      setUser(userRes.data);
      setAllBots(botsRes.data);
      
      if (metricsRes.data) {
        setEngineMetrics(metricsRes.data);
      }
      
      // Calculate strategy confidence from actual bot performance
      calculateStrategyConfidence(botsRes.data);
      
    } catch (error) {
      console.error('Failed to load beta dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStrategyConfidence = (bots) => {
    const strategyStats = {
      orbis_alpha: { total: 0, winRate: 0, pnl: 0 },
      orbis_momentum: { total: 0, winRate: 0, pnl: 0 },
      orbis_defensive: { total: 0, winRate: 0, pnl: 0 },
    };

    bots.forEach((bot) => {
      const strategy = bot.strategy;
      if (strategyStats[strategy]) {
        strategyStats[strategy].total++;
        strategyStats[strategy].winRate += bot.win_rate || 0;
        strategyStats[strategy].pnl += bot.paper_pnl || 0;
      }
    });

    // Calculate confidence scores (0-100)
    const calculateScore = (stats) => {
      if (stats.total === 0) return 85; // Default for new strategies
      const avgWinRate = stats.winRate / stats.total;
      const avgPnl = stats.pnl / stats.total;
      const pnlScore = Math.min(100, Math.max(0, 50 + avgPnl * 5)); // Scale P&L
      return Math.round((avgWinRate * 0.6 + pnlScore * 0.4));
    };

    const alphaScore = calculateScore(strategyStats.orbis_alpha);
    const momentumScore = calculateScore(strategyStats.orbis_momentum);
    const defensiveScore = calculateScore(strategyStats.orbis_defensive);
    const overall = Math.round((alphaScore + momentumScore + defensiveScore) / 3);

    setStrategyConfidence({
      overall,
      alpha: alphaScore,
      momentum: momentumScore,
      defensive: defensiveScore,
    });
  };

  const getConfidenceColor = (score) => {
    if (score >= 85) return 'text-green-500';
    if (score >= 70) return 'text-[#C9A24D]';
    return 'text-red-500';
  };

  const getConfidenceBadge = (score) => {
    if (score >= 85) return { text: 'High', color: 'bg-green-500' };
    if (score >= 70) return { text: 'Medium', color: 'bg-[#C9A24D]' };
    return { text: 'Low', color: 'bg-red-500' };
  };

  const handleLogout = () => {
    localStorage.removeItem('apexion_token');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center">
        <div className="text-[#2F6BFF] text-lg">Loading Beta Dashboard...</div>
      </div>
    );
  }

  const activeBots = allBots.filter((b) => b.status === 'active');
  const pausedBots = allBots.filter((b) => b.status === 'paused');
  const totalPnl = allBots.reduce((sum, b) => sum + (b.paper_pnl || 0), 0);
  const totalTrades = allBots.reduce((sum, b) => sum + (b.total_trades || 0), 0);

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="text-2xl font-bold font-['Outfit'] tracking-tight">APEXION</div>
            <Badge className="bg-[#C9A24D] text-[#0B0D12] rounded-sm">BETA ADMIN</Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#94A3B8]">Live Trading:</span>
              <Badge className="bg-red-500 rounded-sm">OFF</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#94A3B8]">Exchange:</span>
              <Badge className="bg-[#2F6BFF] rounded-sm">Kraken</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#94A3B8]">Confidence:</span>
              <Badge className={`${getConfidenceBadge(strategyConfidence.overall).color} rounded-sm`}>
                {strategyConfidence.overall}%
              </Badge>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: <Activity className="w-5 h-5" />, label: 'Active Bots', value: activeBots.length, color: 'text-green-500' },
            { icon: <Clock className="w-5 h-5" />, label: 'Paused Bots', value: pausedBots.length, color: 'text-yellow-500' },
            { icon: <TrendingUp className="w-5 h-5" />, label: 'Total Trades', value: totalTrades },
            { icon: <BarChart3 className="w-5 h-5" />, label: 'Total P&L', value: `£${totalPnl.toFixed(2)}`, color: totalPnl >= 0 ? 'text-green-500' : 'text-red-500' },
          ].map((stat, idx) => (
            <motion.div key={idx} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: idx * 0.1 }}>
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#94A3B8]">{stat.label}</span>
                    <div className="text-[#2F6BFF]">{stat.icon}</div>
                  </div>
                  <div className={`text-2xl font-bold font-['JetBrains_Mono'] ${stat.color || ''}`}>{stat.value}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Active Bots & Risk */}
          <div className="space-y-6">
            {/* Active Bots */}
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Active Bots</CardTitle>
                <CardDescription className="text-[#94A3B8]">Real-time bot status and performance</CardDescription>
              </CardHeader>
              <CardContent>
                {allBots.length === 0 ? (
                  <p className="text-[#94A3B8] text-center py-8">No bots created yet</p>
                ) : (
                  <div className="space-y-3">
                    {allBots.map((bot) => (
                      <div key={bot.id} className="p-4 bg-[#0B0D12] border border-[#1E293B] rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold capitalize">{bot.strategy.replace('_', ' ')}</span>
                            <Badge
                              className={`${
                                bot.status === 'active'
                                  ? 'bg-green-500'
                                  : bot.status === 'paused'
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              } rounded-sm text-xs`}
                            >
                              {bot.status}
                            </Badge>
                          </div>
                          <span className="text-sm text-[#94A3B8]">{bot.allocation_percentage}% allocated</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                          <div>
                            <p className="text-xs text-[#94A3B8]">P&L</p>
                            <p
                              className={`text-sm font-bold font-['JetBrains_Mono'] ${
                                bot.paper_pnl >= 0 ? 'text-green-500' : 'text-red-500'
                              }`}
                            >
                              ${bot.paper_pnl.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Trades</p>
                            <p className="text-sm font-semibold">{bot.total_trades}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Win Rate</p>
                            <p className="text-sm font-semibold">{bot.win_rate.toFixed(1)}%</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Risk Control Summary */}
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#2F6BFF]" />
                  Risk Control Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Max Daily Loss</span>
                  <span className="font-semibold">5%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Stop-Loss Triggered</span>
                  <span className="font-semibold">0/{allBots.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Kill Switch</span>
                  <Badge className="bg-green-500/20 text-green-400 rounded-sm">Inactive</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Active Bots</span>
                  <span className="font-semibold">{activeBots.length}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Trading Engine Metrics */}
          <div className="space-y-6">
            {/* Trading Engine Status */}
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#2F6BFF]" />
                  Trading Engine Status
                </CardTitle>
                <CardDescription className="text-[#94A3B8]">Real-time engine performance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Last Cycle</span>
                  <span className="font-semibold">60s ago</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Successful Trades</span>
                  <span className="font-semibold text-green-500">{totalTrades}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Failed Trades</span>
                  <span className="font-semibold">0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Exchange Errors</span>
                  <span className="font-semibold">0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Trade Frequency Control</span>
                  <Badge className="bg-green-500/20 text-green-400 rounded-sm">ACTIVE</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Strategy Confidence Score */}
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Strategy Confidence Score</CardTitle>
                <CardDescription className="text-[#94A3B8]">Performance-based confidence indicators</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { name: 'Orbis Alpha', score: strategyConfidence.alpha, key: 'alpha' },
                  { name: 'Orbis Momentum', score: strategyConfidence.momentum, key: 'momentum' },
                  { name: 'Orbis Defensive', score: strategyConfidence.defensive, key: 'defensive' },
                ].map((strategy) => {
                  const badge = getConfidenceBadge(strategy.score);
                  return (
                    <div key={strategy.key}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{strategy.name}</span>
                          <Badge className={`${badge.color} rounded-sm text-xs`}>{badge.text}</Badge>
                        </div>
                        <span className={`text-sm font-bold ${getConfidenceColor(strategy.score)}`}>
                          {strategy.score}%
                        </span>
                      </div>
                      <Progress value={strategy.score} className="h-2" />
                    </div>
                  );
                })}
                <div className="pt-4 border-t border-[#1E293B]">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Overall Confidence</span>
                    <span className={`text-lg font-bold ${getConfidenceColor(strategyConfidence.overall)}`}>
                      {strategyConfidence.overall}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Alerts & Notifications */}
        <Card className="bg-[#15171C] border-[#1E293B] mt-8">
          <CardHeader>
            <CardTitle className="font-['Outfit']">Alerts & Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { icon: <CheckCircle2 className="w-5 h-5 text-green-500" />, text: 'Paper trading mode active', type: 'info' },
                { icon: <Activity className="w-5 h-5 text-[#2F6BFF]" />, text: `${totalTrades} trades executed successfully`, type: 'success' },
                { icon: <Shield className="w-5 h-5 text-green-500" />, text: 'All risk controls active', type: 'success' },
              ].map((alert, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-[#0B0D12] border border-[#1E293B] rounded-sm">
                  {alert.icon}
                  <span className="text-sm">{alert.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Back to User Dashboard */}
        <div className="mt-8 text-center">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-[#1E293B] rounded-sm">
            Back to User Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BetaDashboard;